import { getDB } from '../db/direct-couchdb';

class SubscriptionService {
  constructor() {
    this._plansCache = null;
    this._plansCacheKey = null;
  }

  // Get current DB instance with auth headers
  get db() {
    return getDB();
  }

  // Get subscription plans with current pricing
  async getSubscriptionPlans() {
    const cacheKey = sessionStorage.getItem('customerId') || 'guest';
    if (this._plansCache && this._plansCacheKey === cacheKey) {
      return this._plansCache;
    }
    try {
      // Check if user is authenticated
      const token = localStorage.getItem('token');
      
      let adminData = null;
      if (token) {
        try {
          adminData = await this.db.get('admin_data');
        } catch (error) {
          // admin_data not found or not accessible, use defaults
          adminData = null;
        }
      }

      const pricing = adminData?.subscriptionPricing || null;
      const offerSettings = adminData?.offerSettings || {
        offerName: 'Immunity Shield',
        discountPercentage: 20,
        showOffer: true
      };

      let plans = this.getDefaultPlans();
      
      // Only apply admin pricing override if admin_data was actually fetched
      if (pricing) {
        plans = plans.map(plan => {
          if (plan.planType === 'premium') {
            return {
              ...plan,
              premiumPrice: pricing.premiumPrice,
              monthlyPrice: plan.premiumPerMonth * pricing.premiumPrice,
              offerSettings
            };
          } else if (plan.contactOnly || plan.isTrial) {
            // Custom and trial plans have fixed pricing — do not override
            return { ...plan, offerSettings };
          } else {
            const baseTotal = (plan.shotsPerMonth * pricing.shotPrice) + (plan.smoothiesPerMonth * pricing.smoothiePrice);
            let newMonthlyPrice = baseTotal;
            let newOriginalPrice = plan.originalPrice || null;
            
            // Apply discount if offer settings exist and showOffer is true
            if (offerSettings && offerSettings.showOffer && offerSettings.discountPercentage) {
              newOriginalPrice = baseTotal; // Update original price dynamically based on item prices
              // Apply discount percentage to calculate actual monthly price
              newMonthlyPrice = Math.round(baseTotal * (1 - (offerSettings.discountPercentage / 100)));
            }

            return {
              ...plan,
              shotPrice: pricing.shotPrice,
              smoothiePrice: pricing.smoothiePrice,
              monthlyPrice: newMonthlyPrice,
              originalPrice: newOriginalPrice,
              offerSettings
            };
          }
        });
      } else {
        // Add offer settings to default plans
        plans = plans.map(plan => ({ ...plan, offerSettings }));
      }
      
      this._plansCache = plans;
      this._plansCacheKey = cacheKey;
      return plans;
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
      return this.getDefaultPlans();
    }
  }

  getDefaultPlans() {
    return [
      {
        _id: 'plan_free_trial',
        type: 'subscription_plan',
        planType: 'free_trial',
        name: '🎁 Welcome Gift',
        description: 'Try 5 immunity shots FREE for 3 days!',
        shotPrice: 0,
        smoothiePrice: 0,
        shotsPerMonth: 5,
        smoothiesPerMonth: 0,
        monthlyPrice: 55,
        frequency: '3 Days Only',
        totalUnits: 5,
        isTrial: true,
        featured: true,
        validityDays: 3,
        maxShotsPerDay: 2
      },
      {
        _id: 'plan_immunity_only',
        type: 'subscription_plan',
        planType: 'immunity_only',
        name: 'Immunity Booster Plan',
        description: 'Start your mornings with a powerful shot of nature. Our immunity shots are cold-pressed from roots, herbs and greens — designed to strengthen your body\'s defences, reduce inflammation and keep you energised through the week.',
        shotPrice: 75,
        smoothiePrice: 120,
        shotsPerMonth: 20,
        smoothiesPerMonth: 0,
        monthlyPrice: 2000,
        originalPrice: 2500,
        frequency: 'Monday-Friday',
        billingCycle: 'monthly',
        servingsPerWeek: 5,
        featured: true
      },
      {
        _id: 'plan_complete_wellness',
        type: 'subscription_plan',
        planType: 'complete_wellness',
        name: 'Custom Wellness Bundle',
        description: 'A personalised plan crafted for your unique health needs. Whatever your health goal or condition — we discuss, we customise, we deliver.',
        shotPrice: 60,
        smoothiePrice: 0,
        shotsPerMonth: 20,
        smoothiesPerMonth: 0,
        monthlyPrice: 5000,
        frequency: 'Monday-Friday',
        billingCycle: 'monthly',
        servingsPerWeek: 5,
        isCustom: true,
        contactOnly: true
      },
      {
        _id: 'plan_premium',
        type: 'subscription_plan',
        planType: 'premium',
        name: 'Premium',
        description: 'Exclusive premium products delivered daily',
        premiumPrice: 60,
        premiumPerMonth: 40,
        monthlyPrice: 2400,
        frequency: 'Monday-Friday',
        billingCycle: 'monthly',
        servingsPerWeek: 5
      }
    ];
  }

  // Update subscription plan pricing (Admin only)
  async updatePlanPricing(planType, shotPrice, smoothiePrice, premiumPrice = null) {
    try {
      // Get or create admin_data document
      let adminData;
      try {
        adminData = await this.db.get('admin_data');
      } catch (error) {
        if (error.status === 404) {
          // Create new admin_data if it doesn't exist
          adminData = {
            _id: 'admin_data',
            type: 'admin_settings',
            subscriptionPricing: {
              shotPrice: 75,
              smoothiePrice: 120,
              premiumPrice: 100
            },
            offerSettings: {
              offerName: 'Monsoon Immunity Shield',
              discountPercentage: 33.33,
              showOffer: true
            }
          };
          await this.db.put(adminData);
          return adminData;
        } else {
          throw error;
        }
      }

      // Update all pricing at once
      adminData.subscriptionPricing = {
        shotPrice: shotPrice,
        smoothiePrice: smoothiePrice,
        premiumPrice: premiumPrice
      };

      await this.db.put(adminData);
      this._plansCache = null; // invalidate cache
      return adminData;
    } catch (error) {
      console.error('Error updating plan pricing:', error);
      throw error;
    }
  }

  // Update offer settings (Admin only)
  async updateOfferSettings(offerName, discountPercentage, showOffer) {
    try {
      let adminData;
      try {
        adminData = await this.db.get('admin_data');
      } catch (error) {
        if (error.status === 404) {
          adminData = {
            _id: 'admin_data',
            type: 'admin_settings',
            subscriptionPricing: {
              shotPrice: 75,
              smoothiePrice: 120,
              premiumPrice: 100
            },
            offerSettings: {
              offerName: offerName,
              discountPercentage: discountPercentage,
              showOffer: showOffer
            }
          };
          await this.db.put(adminData);
          return adminData;
        } else {
          throw error;
        }
      }

      adminData.offerSettings = {
        offerName: offerName,
        discountPercentage: discountPercentage,
        showOffer: showOffer
      };

      await this.db.put(adminData);
      this._plansCache = null;
      return adminData;
    } catch (error) {
      console.error('Error updating offer settings:', error);
      throw error;
    }
  }

  // Create user subscription
  async createSubscription(customerId, planType, selectedProducts, selectedAddress = null, deliverySchedule = {}) {
    try {
      const plans = await this.getSubscriptionPlans();
      const plan = plans.find(p => p.planType === planType);
      
      const subscription = {
        _id: `subscription_${customerId}_${Date.now()}`,
        type: 'user_subscription',
        customerId,
        planType,
        planName: plan.name,
        monthlyPrice: plan.monthlyPrice,
        selectedProducts,
        deliverySchedule,
        deliveryAddress: selectedAddress,
        status: 'active',
        startDate: new Date().toISOString(),
        nextBillingDate: this.getNextBillingDate(),
        nextDeliveryDate: this.getNextDeliveryDate(),
        deliveriesCompleted: 0,
        deliveriesRemaining: 20,
        createdAt: new Date().toISOString()
      };

      await this.db.put(subscription);
      return subscription;
    } catch (error) {
      console.error('Error creating subscription:', error);
      throw error;
    }
  }

  // Check if user has any subscriptions
  async hasUserSubscriptions(customerId) {
    try {
      const subscriptions = await this.getUserSubscriptions(customerId);
      return subscriptions.length > 0;
    } catch (error) {
      console.error('Error checking user subscriptions:', error);
      return false;
    }
  }

  // Get user subscriptions — fetches from backend API to include server-created trial subscriptions
  async getUserSubscriptions(customerId) {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || '';

      const response = await fetch(`${apiUrl}/api/_find`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          selector: {
            type: 'user_subscription',
            customerId
          }
        })
      });

      if (!response.ok) throw new Error(`API error ${response.status}`);
      const data = await response.json();
      const allUserSubscriptions = data.docs || [];

      // Sort client-side — avoids needing a CouchDB index
      const activeSubscriptions = allUserSubscriptions
        .filter(sub => sub.status === 'active' || sub.status === 'paused')
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      const endedSubscriptions = allUserSubscriptions
        .filter(sub => sub.status === 'cancelled' || sub.status === 'completed')
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      return [
        ...activeSubscriptions,
        ...(endedSubscriptions.length > 0 ? [endedSubscriptions[0]] : [])
      ];
    } catch (error) {
      console.error('Error fetching user subscriptions:', error);
      return [];
    }
  }

  // Get all subscriptions (Admin)
  async getAllSubscriptions() {
    try {
      const result = await this.db.allDocs({
        include_docs: true,
        startkey: 'subscription_',
        endkey: 'subscription_\ufff0'
      });
      
      const allSubscriptions = result.rows
        .map(row => row.doc)
        .filter(doc => doc && doc.type === 'user_subscription')
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        
      return allSubscriptions;
    } catch (error) {
      console.error('Error fetching all subscriptions:', error);
      return [];
    }
  }

  // Get subscription by ID
  async getSubscriptionById(subscriptionId) {
    try {
      return await this.db.get(subscriptionId);
    } catch (error) {
      console.error('Error getting subscription by ID:', error);
      throw error;
    }
  }

  // Update subscription
  async updateSubscription(subscription) {
    try {
      subscription.updatedAt = new Date().toISOString();
      await this.db.put(subscription);
      return subscription;
    } catch (error) {
      console.error('Error updating subscription:', error);
      throw error;
    }
  }

  // Update subscription status
  async updateSubscriptionStatus(subscriptionId, status) {
    try {
      const subscription = await this.db.get(subscriptionId);
      subscription.status = status;
      subscription.updatedAt = new Date().toISOString();
      await this.db.put(subscription);
      return subscription;
    } catch (error) {
      console.error('Error updating subscription status:', error);
      throw error;
    }
  }

  // Update delivery status for a specific date
  async updateDeliveryStatus(subscriptionId, dateKey, status) {
    try {
      const subscription = await this.db.get(subscriptionId);
      if (subscription.deliverySchedule && subscription.deliverySchedule[dateKey]) {
        subscription.deliverySchedule[dateKey].status = status;
        subscription.updatedAt = new Date().toISOString();
        await this.db.put(subscription);
      }
      return subscription;
    } catch (error) {
      console.error('Error updating delivery status:', error);
      throw error;
    }
  }

  // Swap delivery product
  async swapDeliveryProduct(subscriptionId, dateKey, productType, newProductId) {
    try {
      const subscription = await this.db.get(subscriptionId);
      if (subscription.deliverySchedule && subscription.deliverySchedule[dateKey]) {
        subscription.deliverySchedule[dateKey][productType] = newProductId;
        subscription.updatedAt = new Date().toISOString();
        await this.db.put(subscription);
      }
      return subscription;
    } catch (error) {
      console.error('Error swapping delivery product:', error);
      throw error;
    }
  }

  // Check if a delivery date can be edited (2+ days in advance)
  canEditDeliveryDate(dateKey) {
    const deliveryDate = new Date(dateKey);
    const minEditDate = new Date();
    minEditDate.setDate(minEditDate.getDate() + 2);
    minEditDate.setHours(0, 0, 0, 0);
    deliveryDate.setHours(0, 0, 0, 0);
    return deliveryDate >= minEditDate;
  }

  // Update product selection for a specific delivery date
  async updateDeliveryProduct(subscriptionId, dateKey, productType, productId) {
    try {
      // Validate: Can only edit 2+ days ahead
      if (!this.canEditDeliveryDate(dateKey)) {
        throw new Error('Can only edit deliveries 2 or more days in advance');
      }

      const subscription = await this.db.get(subscriptionId);
      
      // Check if delivery schedule exists
      if (!subscription.deliverySchedule || !subscription.deliverySchedule[dateKey]) {
        throw new Error('Delivery date not found in schedule');
      }

      // Check if delivery is already completed
      if (subscription.deliverySchedule[dateKey].status === 'delivered') {
        throw new Error('Cannot edit a delivery that has already been completed');
      }

      // Update the product
      subscription.deliverySchedule[dateKey][productType] = productId;
      subscription.updatedAt = new Date().toISOString();
      
      await this.db.put(subscription);
      return subscription;
    } catch (error) {
      console.error('Error updating delivery product:', error);
      throw error;
    }
  }

  getNextBillingDate() {
    const date = new Date();
    date.setMonth(date.getMonth() + 1);
    return date.toISOString();
  }

  getNextDeliveryDate() {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    // Skip weekends
    while (date.getDay() === 0 || date.getDay() === 6) {
      date.setDate(date.getDate() + 1);
    }
    return date.toISOString();
  }
}

export default new SubscriptionService();